import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Menu,
  MenuItem,
  Link,
} from '@mui/material';
import { NavigateNext, MoreHoriz, Home } from '@mui/icons-material';
import { useBreadcrumbs } from '../../contexts/BreadcrumbContext';

const MAX_VISIBLE = 4; // Show at most this many crumbs before collapsing

function Breadcrumbs() {
  const { trail } = useBreadcrumbs();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);

  // Always show "Search" as the root, then the trail
  // If no trail, don't render breadcrumbs at all (we're on the search page)
  if (trail.length === 0) return null;

  const handleClick = (path) => {
    navigate(path);
  };

  // Determine which crumbs to show vs collapse
  let visibleCrumbs = trail;
  let collapsedCrumbs = [];

  if (trail.length > MAX_VISIBLE) {
    // Show first crumb, ellipsis, then last (MAX_VISIBLE - 2) crumbs
    collapsedCrumbs = trail.slice(0, trail.length - (MAX_VISIBLE - 1));
    visibleCrumbs = trail.slice(trail.length - (MAX_VISIBLE - 1));
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        px: 2,
        py: 0.75,
        maxWidth: 1200,
        width: '100%',
        mx: 'auto',
        flexWrap: 'wrap',
      }}
    >
      {/* Search root */}
      <Link
        component="button"
        variant="body2"
        underline="hover"
        onClick={() => handleClick('/')}
        sx={{
          color: 'primary.main',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          fontWeight: 500,
        }}
      >
        <Home sx={{ fontSize: 16 }} />
        Search
      </Link>

      {/* Collapsed crumbs ellipsis */}
      {collapsedCrumbs.length > 0 && (
        <>
          <NavigateNext sx={{ fontSize: 16, color: 'text.disabled' }} />
          <Link
            component="button"
            variant="body2"
            underline="hover"
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{ color: 'text.secondary', cursor: 'pointer', minWidth: 24, textAlign: 'center' }}
          >
            <MoreHoriz sx={{ fontSize: 18, verticalAlign: 'middle' }} />
          </Link>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            {collapsedCrumbs.map((crumb) => (
              <MenuItem
                key={crumb.key}
                onClick={() => {
                  setAnchorEl(null);
                  handleClick(crumb.path);
                }}
                sx={{ fontSize: '0.85rem' }}
              >
                {crumb.label}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}

      {/* Visible crumbs */}
      {visibleCrumbs.map((crumb, i) => {
        const isLast = i === visibleCrumbs.length - 1;
        return (
          <React.Fragment key={crumb.key}>
            <NavigateNext sx={{ fontSize: 16, color: 'text.disabled' }} />
            {isLast ? (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.primary',
                  fontWeight: 500,
                  maxWidth: { xs: 150, sm: 250 },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {crumb.label}
              </Typography>
            ) : (
              <Link
                component="button"
                variant="body2"
                underline="hover"
                onClick={() => handleClick(crumb.path)}
                sx={{
                  color: 'primary.main',
                  cursor: 'pointer',
                  maxWidth: { xs: 120, sm: 200 },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {crumb.label}
              </Link>
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
}

export default Breadcrumbs;
